import type { RefObject } from 'react'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TaskDetailDialog from 'src/components/GenerationTask/TaskDetailDialog'
import { useMyInfo } from 'src/services/apollo/auth'
import {
  executeUpdateBookmarkItem,
  useListMyBookmarkItems,
} from 'src/services/apollo/bookmark'
import type { BookmarkItem, Task } from 'src/services/apollo/generated/graphql'
import { RefType } from 'src/services/apollo/generated/graphql'
import { useQs } from 'src/utils/qs'
import type {
  BaseCursorPagingContextType,
  BaseCursorPagingRef,
} from '../CursorPaging/BaseCursorPaging'
import BaseCursorPaging from '../CursorPaging/BaseCursorPaging'
import Empty from '../Empty'
import TaskPreviewCard from '../GenerationTask/TaskPreviewCard'
import LoadingPage from '../LoadingPage'
import NotLogin from '../NotLogin'
import { useEditItems } from './EditItems'

export default function BookmarkItemList() {
  const pagingRef = useRef<BaseCursorPagingRef>(null)
  const { t } = useTranslation(['artwork', 'user'])

  const editState = useEditItems({
    actionButtonName: t('user:profile-sub-tab.tasks.manage.bookmark-action'),
    deleteItem: itemId => {
      const splited = itemId.split('-')
      return executeUpdateBookmarkItem({
        input: {
          taskId: splited[0],
          mediaId: splited[1],
          remove: true,
        },
      })
    },
    afterDelete: () => {
      pagingRef.current?.mutate()
    },
    dialogTitle: 'Confirm to remove these from favorites?',
    dialogContent: '',
  })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4">
        <div className="flex-1"></div>
        {editState.editItemsAction}
      </div>
      <BookmarkItemListContent
        pagingRef={pagingRef}
        editing={editState.editing}
        selectedItems={editState.selectedItems}
        toggleSelected={editState.toggleSelected}
      />
    </div>
  )
}

function BookmarkItemListContent(props: {
  editing: boolean
  selectedItems: string[]
  toggleSelected: (id: string) => void
  pagingRef: RefObject<BaseCursorPagingContextType | null>
}) {
  const { editing, selectedItems, toggleSelected, pagingRef } = props
  const { t } = useTranslation(['artwork', 'user'])
  const [{ before, after }, setQs] = useQs()

  const { data: userInfo, loading } = useMyInfo()

  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [activeTask, setActiveTask] = useState<Task>()

  const onClickTask = useCallback((task: Task) => {
    setActiveTask(task)
    setIsDetailOpen(true)
  }, [])

  if (!userInfo) {
    if (loading) return <LoadingPage />
    return <NotLogin />
  }

  const tapItem = (item: BookmarkItem) => {
    if (editing) {
      toggleSelected(`${item.task?.id ?? ''}-${item.mediaId ?? ''}`)
    } else {
      onClickTask(item.task!)
    }
  }

  return (
    <>
      <BaseCursorPaging
        useSWR={useListMyBookmarkItems}
        getConnection={r => r}
        pageSize={20}
        pagingRef={pagingRef}
        renderItem={(item: BookmarkItem) => {
          return (
            <BookmarkPreviewCard
              item={item}
              onItemClick={tapItem}
              key={item.id}
              selected={selectedItems.includes(
                `${item.task?.id ?? ''}-${item.mediaId ?? ''}`,
              )}
              isEdit={editing}
            />
          )
        }}
        empty={<Empty description={t('user:favorite-list.placeholder')} />}
        before={before}
        after={after}
        onCursorChange={setQs}
        extraArgs={{
          types: [RefType.Task],
        }}
      />
      <TaskDetailDialog
        task={activeTask}
        open={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
      />
    </>
  )
}

function BookmarkPreviewCard(props: {
  item: BookmarkItem
  onItemClick: (item: BookmarkItem) => void
  selected: boolean
  isEdit: boolean
}) {
  const { item, onItemClick, selected, isEdit } = props

  const onClick = useCallback(() => {
    onItemClick(item)
  }, [item, onItemClick])

  return (
    <TaskPreviewCard
      isEdit={isEdit}
      selected={selected}
      task={item.task!}
      time={item.createdAt}
      onClick={onClick}
      mediaId={
        (item.task?.parameters.batchSize ?? 0) > 1
          ? item.mediaId
          : item.task?.mediaId
      }
    />
  )
}
